import { Injectable, OnDestroy } from '@angular/core';
import { Subject, Subscription, fromEvent } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { EchoService } from '../echo.service';
import { LegFiJwtService } from '../auth/legfi-jwt.service';
import { OrgContextService } from './org-context.service';
import { ApplicationHttpClient } from '../../components/shared/http/application-http-client';
import { Routes } from '../../config/routes';
import { environment } from 'environments/environment';

/**
 * Organization broadcast event types
 */
export enum OrganizationEventType {
    ORGANIZATION_UPDATED = 'organization:updated',
    UNIT_CREATED = 'organization:unit:created',
    UNIT_UPDATED = 'organization:unit:updated',
    UNIT_DELETED = 'organization:unit:deleted',
    UNITS_BULK_UPDATED = 'organization:units:bulk-updated',
    FEATURE_FLAG_ATTACHED = 'organization:feature-flag:attached',
    FEATURE_FLAG_DETACHED = 'organization:feature-flag:detached',
    AMENITY_STATS_UPDATED = 'organization:amenity:stats:updated',
    TREASURY_TRANSFER_UPDATED = 'organization:treasury:transfer:updated',
    TREASURY_BALANCE_UPDATED = 'organization:treasury:balance:updated',
    TREASURY_CARD_UPDATED = 'organization:treasury:card:updated',
    TREASURY_CONNECT_STATUS_UPDATED = 'organization:treasury:connect-status:updated',
    TREASURY_FINANCIAL_ACCOUNT_READY = 'organization:treasury:financial-account:ready',
    TREASURY_BANK_ACCOUNTS_UPDATED = 'organization:treasury:bank-accounts:updated',
}

/**
 * Organization broadcast event payload
 */
export interface OrganizationBroadcastEvent {
    type: OrganizationEventType;
    organizationId: number;
    timestamp: number;
    data?: any;
}

/**
 * Service for broadcasting and listening to organization-level events
 * Uses BroadcastChannel API for cross-tab communication
 */
@Injectable({
    providedIn: 'root',
})
export class OrganizationBroadcastService implements OnDestroy
{
    private readonly CHANNEL_NAME = 'organization-events';
    private broadcastChannel: BroadcastChannel | null = null;
    private eventSubject$ = new Subject<OrganizationBroadcastEvent>();
    private jwtRefreshedSubject$ = new Subject<void>();
    private echoChannel: any = null;

    // SD-8086: the org id whose Echo channel we are currently joined to (null when none). Tracked
    // explicitly because this root singleton never tears down, so re-subscribe logic must leave the
    // OLD channel itself rather than relying on ngOnDestroy (which never fires for a root service).
    private _joinedOrgId: number | null = null;

    // Cross-tab + feature-flag listeners are org-independent; wire them once, on the first admin/super
    // join, so a non-admin session behaves as before (no listeners).
    private _localListenersInitialized = false;

    private _orgContextSub: Subscription | null = null;

    private _reestablishedSub: Subscription | null = null;

    constructor(
        private echoService: EchoService,
        private _http: ApplicationHttpClient,
        private _orgContext: OrgContextService,
    ) {
        // SD-8086: (re)subscribe the org Echo channel on every org-context transition — fresh signup,
        // org switch, and PMC->child. currentOrgId$ is BehaviorSubject-backed, so it replays the current
        // org id on subscribe; that covers a session that authed BEFORE this root singleton was built
        // (the fresh-signup race). The admin/superUser gate is re-checked per emission because those
        // flags live in the per-org token and change on switch / PMC->child.
        this._orgContextSub = this._orgContext.currentOrgId$.subscribe(orgId => this.syncEchoChannel(orgId));

        // SD-7778: re-join the org channel after the shared Echo instance is rebuilt on a
        // login / re-login (logout now tears the instance down to reclaim its Ably
        // connection). currentOrgId$ alone cannot cover a SAME-org re-login — orgContextChanged$
        // stays silent when the org is unchanged, so currentOrgId$ never re-emits — and even
        // when it does, our _joinedOrgId still points at the pre-logout org, so syncEchoChannel's
        // same-org guard would skip the re-join. Reset the tracker so the join is forced, then
        // reconcile to the live org. reestablished$ fires ONLY on a re-login, never on an
        // in-session org switch, so this never double-runs with the currentOrgId$ path during a
        // switch; a different-org re-login fires both, but leaveEchoChannel-first + the guard
        // make the overlap idempotent.
        this._reestablishedSub = this.echoService.reestablished$.subscribe(() => {
            this._joinedOrgId = null;
            this.syncEchoChannel(this._orgContext.currentOrgId);
        });
    }

    ngOnDestroy(): void {
        this._orgContextSub?.unsubscribe();
        this._orgContextSub = null;

        this._reestablishedSub?.unsubscribe();
        this._reestablishedSub = null;

        // Leave the currently-joined Echo channel (tracked id, not the live JWT).
        this.leaveEchoChannel();

        // Clean up BroadcastChannel
        if (this.broadcastChannel) {
            this.broadcastChannel.close();
            this.broadcastChannel = null;
        }

        // Complete the event subject
        this.eventSubject$.complete();
        this.jwtRefreshedSubject$.complete();
    }

    /**
     * Manual cleanup method for testing purposes
     * (Root services' ngOnDestroy is never called automatically)
     */
    public manualCleanup(): void {
        this.ngOnDestroy();
    }

    /**
     * Observable that emits when JWT has been refreshed due to feature flag changes
     * Services can subscribe to this to reinitialize when feature flags change
     */
    public onJwtRefreshed$() {
        return this.jwtRefreshedSubject$.asObservable();
    }

    /**
     * Listen to all organization events
     */
    public onEvent$() {
        return this.eventSubject$.asObservable();
    }

    /**
     * Listen to specific event type
     */
    public onEventType$(type: OrganizationEventType) {
        return this.eventSubject$.asObservable().pipe(
            filter(event => event.type === type)
        );
    }

    /**
     * Listen to events for a specific organization
     */
    public onOrganizationEvents$(organizationId: number) {
        return this.eventSubject$.asObservable().pipe(
            filter(event => event.organizationId === organizationId)
        );
    }

    /**
     * Listen to unit-related events for a specific organization
     */
    public onOrganizationUnitEvents$(organizationId: number) {
        return this.eventSubject$.asObservable().pipe(
            filter(event =>
                event.organizationId === organizationId &&
                (
                    event.type === OrganizationEventType.UNIT_CREATED ||
                    event.type === OrganizationEventType.UNIT_UPDATED ||
                    event.type === OrganizationEventType.UNIT_DELETED ||
                    event.type === OrganizationEventType.UNITS_BULK_UPDATED
                )
            )
        );
    }

    /**
     * SD-8086: wire the org-independent local listeners (cross-tab BroadcastChannel + feature-flag
     * JWT refresh) exactly once, on the first admin/super join. Keeps a non-admin session listener-free
     * (the original construction-time gating) while surviving re-subscribes across org transitions.
     */
    private ensureLocalListeners(): void {
        if (this._localListenersInitialized) {
            return;
        }
        this.initializeBroadcastChannel();
        this.initializeFeatureFlagListener();
        this._localListenersInitialized = true;
    }

    /**
     * Initialize BroadcastChannel if supported
     */
    private initializeBroadcastChannel(): void {
        // Check if BroadcastChannel is supported
        if (typeof BroadcastChannel !== 'undefined') {
            this.broadcastChannel = new BroadcastChannel(this.CHANNEL_NAME);

            // Listen to messages from other tabs
            fromEvent<MessageEvent>(this.broadcastChannel, 'message')
                .pipe(
                    map(event => event.data as OrganizationBroadcastEvent),
                    filter(event => this.isValidEvent(event))
                )
                .subscribe(event => {
                    this.eventSubject$.next(event);
                });
        }
    }

    /**
     * SD-8086: reconcile the joined Echo channel to the current org context. Called on every
     * `currentOrgId$` emission (fresh signup, org switch, PMC->child, logout). Leaves the previously
     * joined channel before joining the new one, and re-checks the admin/superUser gate per emission.
     */
    private syncEchoChannel(orgId: number | null): void {
        // Already reconciled to this org (or both null) — nothing to do. Guards against same-channel
        // leave->join thrash and duplicate `.listen` bindings.
        if (orgId === this._joinedOrgId) {
            return;
        }

        // Leave whatever channel we're currently on before switching orgs.
        this.leaveEchoChannel();

        // Logged out / org-less: leave only, never join (a null org id would register `Organization.null`
        // and fire a bearer-less broadcasting auth).
        if (orgId == null) {
            return;
        }

        // Per-emission gate: only admin/superUser sessions join the org channel (Treasury KYC / Add
        // Charge are admin-only flows; the flags are per-org token so this must be re-checked here).
        const jwt = LegFiJwtService.read();
        if (!jwt?.admin && !jwt?.superUser) {
            return;
        }

        this.joinEchoChannel(orgId);
    }

    /**
     * Leave the currently-joined org Echo channel (if any) and reset tracking. Safe to call when
     * nothing is joined.
     */
    private leaveEchoChannel(): void {
        if (this._joinedOrgId != null) {
            try {
                this.echoService.leave(`Organization.${this._joinedOrgId}`);
            } catch {
                // Swallow: a benign leave race must not tear down the currentOrgId$ subscription
                // (that would kill re-subscribes for the rest of the session). EchoService.leave
                // already emits the dev-only breadcrumb, so we don't log again here.
            }
        }
        this.echoChannel = null;
        this._joinedOrgId = null;
    }

    /**
     * Join the private Echo channel for the given org and register the org-event listeners.
     */
    private joinEchoChannel(orgId: number): void {
        // Cross-tab + feature-flag listeners are wired lazily on the first admin/super join so a
        // non-admin session keeps the original (no-listener) behavior.
        this.ensureLocalListeners();

        const channelName = `Organization.${orgId}`;

        try {
            this.echoChannel = this.echoService.private(channelName);
            this._joinedOrgId = orgId;

            // Listen for unit events
            this.echoChannel.listen('.unit.created', (data: any) => {
                this.handleEchoEvent(data);
            });

            this.echoChannel.listen('.unit.updated', (data: any) => {
                this.handleEchoEvent(data);
            });

            this.echoChannel.listen('.unit.deleted', (data: any) => {
                this.handleEchoEvent(data);
            });

            this.echoChannel.listen('.units.bulk-updated', (data: any) => {
                this.handleEchoEvent(data);
            });

            // Listen for feature flag events
            this.echoChannel.listen('.feature-flag.attached', (data: any) => {
                this.handleEchoEvent(data);
            });

            this.echoChannel.listen('.feature-flag.detached', (data: any) => {
                this.handleEchoEvent(data);
            });

            // Listen for amenity stats events
            this.echoChannel.listen('.amenity.stats.updated', (data: any) => {
                this.handleEchoEvent(data);
            });

            // Listen for treasury events
            this.echoChannel.listen('.treasury.transfer.updated', (data: any) => {
                this.handleEchoEvent(data);
            });

            this.echoChannel.listen('.treasury.balance.updated', (data: any) => {
                this.handleEchoEvent(data);
            });

            this.echoChannel.listen('.treasury.card.updated', (data: any) => {
                this.handleEchoEvent(data);
            });

            // SD-6865 broadcast adoption: live updates for post-KYC Treasury
            // readiness. Connect-status flips when the org's Stripe Connect
            // capability becomes active; financial-account ready fires when
            // outbound_transfers become active on the DBA.
            //
            // Note: this join is admin/superUser gated per org transition
            // (see syncEchoChannel). Acceptable because Treasury KYC and Add
            // Charge are admin-only flows — the only sessions that observe
            // these broadcasts are the ones that have Echo wired up.
            this.echoChannel.listen('.treasury.connect-status.updated', (data: any) => {
                this.handleEchoEvent(data);
            });

            this.echoChannel.listen('.treasury.financial-account.ready', (data: any) => {
                this.handleEchoEvent(data);
            });

            // SD-7414: post-provisioning external bank accounts added on the
            // KYC review screen fire account.external_account.* webhooks that
            // sync new UnifiedBankAccount rows; this broadcast tells the UI
            // bank-accounts list to refetch so the new account appears
            // without a manual refresh.
            this.echoChannel.listen('.treasury.bank-accounts.updated', (data: any) => {
                this.handleEchoEvent(data);
            });

        } catch (error) {
            this.reportError('OrganizationBroadcastService: Error initializing Echo listener', error);
        }
    }

    /**
     * Initialize listener for feature flag changes
     * Automatically refreshes JWT when feature flags are updated
     */
    private initializeFeatureFlagListener(): void {
        this.eventSubject$.pipe(
            filter(event =>
                event.type === OrganizationEventType.FEATURE_FLAG_ATTACHED ||
                event.type === OrganizationEventType.FEATURE_FLAG_DETACHED
            )
        ).subscribe(event => {
            // Trigger JWT refresh to get updated feature flags
            this.triggerJwtRefresh();
        });
    }

    /**
     * Trigger a lightweight API call to force JWT refresh via RefreshToken middleware
     */
    private triggerJwtRefresh(): void {
        const jwt = LegFiJwtService.read();
        if (!jwt || !jwt.orgId) {
            return;
        }

        // Make a lightweight GET request to force JWT refresh
        // Use a lightweight endpoint that all authenticated users can access
        // The RefreshToken middleware will automatically update the JWT with new feature flags
        const url = Routes.MakeLegFiCoreUrl(`/organizations/${jwt.orgId}`);
        this._http.get(url).subscribe({
            next: () => {
                // Emit event so services can react to the JWT update
                this.jwtRefreshedSubject$.next();
            },
            error: (err) => {
                this.reportError('[OrganizationBroadcast] Failed to refresh JWT', err);
            }
        });
    }

    /**
     * Hook for actionable broadcast errors. The Better Stack client-error sink
     * was removed (SD-7666), so this now emits a dev-only breadcrumb and is
     * otherwise a no-op; the !environment.production gate keeps it out of the
     * prod console (SD-7666 removed SD-7544's global console mask).
     */
    private reportError(message: string, error?: unknown): void {
        if (!environment.production) {
            console.error(message, error);
        }
    }

    /**
     * Handle events from Laravel Echo and propagate to local subscribers
     * Note: We do NOT propagate Echo events to BroadcastChannel because:
     * 1. Laravel Echo already broadcasts to ALL tabs via WebSocket
     * 2. Each tab's Echo listener will receive the event independently
     * 3. BroadcastChannel is only for manual client-side broadcasts
     */
    private handleEchoEvent(data: any): void {
        const event: OrganizationBroadcastEvent = {
            type: data.type as OrganizationEventType,
            organizationId: data.organizationId,
            timestamp: data.timestamp,
            data: data.data,
        };

        // Emit to local subscribers only
        this.eventSubject$.next(event);

        // Do NOT post to BroadcastChannel - Echo already handles cross-tab communication
    }

    /**
     * Validate event structure
     */
    private isValidEvent(event: any): event is OrganizationBroadcastEvent {
        return event
            && typeof event.type === 'string'
            && typeof event.organizationId === 'number'
            && typeof event.timestamp === 'number';
    }
}
